import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { RouteMap } from "../RouteMap/RouteMap";
import { useRouteMapStops } from "../RouteMap/useRouteMapStops";
import { containerOrderAPI } from "../../services/api";
import type { ContainerOrderLine } from "../../types/containerOrders";
import type { PlanningCard } from "../../types/planning";

/**
 * ── Where one container goes, inside its own card ────────────────────────────
 *
 * The board's card opens downwards to show the points this container touches —
 * the terminal it is collected from, the client's site, wherever else it is due
 * — as numbered pins, and nothing else.
 *
 * ── Pins, not a route ────────────────────────────────────────────────────────
 *
 * Deliberately no line between them. At a card's width the road is a scribble
 * that says less than the three points do, and a dispatcher opening a card on
 * this screen is asking "whereabouts is this job", not "which way will the
 * driver go". Skipping it also means no Directions lookup, so opening cards all
 * afternoon costs nothing beyond the map itself. The full run, drawn properly
 * with its roads and distances, is on the container's own page.
 *
 * ── Opened, not shown ────────────────────────────────────────────────────────
 *
 * The stops are not on the board's payload and should not be: a week of the
 * fleet is hundreds of cards and the endpoint sends each one the handful of
 * fields the card reads. So the container is fetched when somebody opens it,
 * and remembered — a dispatcher comparing two jobs flicks between them for
 * free. Only one card is open at a time, so only one map exists.
 */

/** Containers already fetched, by id. Opening the same card twice is free. */
const lineCache = new Map<string, ContainerOrderLine>();

export function PlanningCardMap({ card }: { card: PlanningCard }) {
  const { t } = useTranslation(["calendar", "containerOrders"]);
  const [line, setLine] = useState<ContainerOrderLine | null>(
    () => lineCache.get(card.id) ?? null,
  );
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (lineCache.has(card.id)) {
      setLine(lineCache.get(card.id)!);
      setFailed(false);
      return;
    }

    let cancelled = false;
    setLine(null);
    setFailed(false);

    containerOrderAPI
      .getLine(card.orderId, card.id)
      .then((res) => {
        const fetched = res.data?.data?.line as ContainerOrderLine | undefined;
        if (!fetched) throw new Error("No container in response");
        lineCache.set(card.id, fetched);
        if (!cancelled) setLine(fetched);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [card.id, card.orderId]);

  const route = useRouteMapStops(line);

  if (failed) {
    return (
      <p className="mt-2 rounded border border-dashed border-gray-200 bg-gray-50 py-4 text-center text-[10.5px] text-gray-500">
        {t("planning.card.mapFailed")}
      </p>
    );
  }

  if (!line) {
    return (
      <p className="mt-2 rounded border border-dashed border-gray-200 bg-gray-50 py-4 text-center text-[10.5px] text-gray-400">
        {t("containerOrders:lineRoute.mapLoading")}
      </p>
    );
  }

  return (
    <div className="mt-2">
      <RouteMap
        stops={route.stops}
        titles={route.titles}
        /* On the map, where each stop is. What each stop *is* goes in the list
           below — one label each, rather than both said twice. */
        labels={route.places}
        compact
        pinsOnly
        className="h-44"
      />

      {/*
        The numbers on the pins, spelled out — the stop's *role*, because the
        pin has already said the place. Read together they answer the question
        a dispatcher actually has: the loading site is the one in Peshawar.
      */}
      <ol className="mt-1.5 space-y-0.5">
        {route.stops.map((stop) => (
          <li
            key={stop.id}
            className="flex items-start gap-1.5 text-[10px] leading-tight"
          >
            <span className="mt-px shrink-0 font-mono font-bold text-dark-900">
              {stop.index}.
            </span>
            <span className="min-w-0 truncate text-gray-600">
              {route.titles[stop.id]}
            </span>
          </li>
        ))}
      </ol>

      {route.missing > 0 && (
        <p className="mt-1 text-[10px] text-amber-800">
          {t("containerOrders:lineRoute.mapMissing", { count: route.missing })}
        </p>
      )}
    </div>
  );
}
